/* ============================================================================
   Cutluxe — Accessibility remediation (WCAG 2.2 AA)
   Added 2026-06-27. Site-wide fixes loaded via snippets/custom-code-body.liquid.
   Covers: colour-contrast overrides, form-control labels (cart qty + price
   facets), image alt fallbacks, and valid ARIA roles for star ratings and the
   product-showcase dots. Each fix is idempotent and self-healing via a
   debounced MutationObserver, so it survives async/app-injected content.
   ========================================================================== */
(function () {
  'use strict';

  /* 1. Colour-contrast overrides. Injected as the LAST <style> in <body> so it
        wins the cascade over the bundled .aio.min.css files. All values were
        measured to meet 4.5:1 (normal) / 3:1 (large) against their real
        background. */
  function injectContrastCSS() {
    if (document.getElementById('as-a11y-contrast')) return;
    var css = [
      /* Primary add-to-cart button: white on #ff5501 = 3.21:1 -> #D2400A = 4.69:1.
         transition:none defeats a stuck `transition:all` that otherwise pins the colour above !important. */
      '.as-prod__btn--primary,.m-add-to-cart.as-prod__btn--primary{background-color:#D2400A !important;transition:none !important;}',
      '.as-prod__btn--primary:hover,.m-add-to-cart.as-prod__btn--primary:hover{background-color:#B83800 !important;}',
      /* Sale badge + cart-count bubble: white on #da3f3f = 4.41:1 -> #CC3333 = 5.14:1 */
      '.m-product-tag--sale.m-color-badge-sale{background-color:#CC3333 !important;}',
      '.m-cart-count-bubble{background-color:#CC3333 !important;}',
      /* Struck compare-at price: #bbb = 1.61:1 -> #595959 (5.9:1 on grey cards).
         Theme sets this !important at (0,2,1); selectors below outrank it. */
      '.m-price__sale s.m-price-item.m-price-item--regular,.m-price__sale del.m-price-item,s.m-price-item.m-price-item--regular,del.m-price-item{color:#595959 !important;}',
      /* Discount price (orange on light grey): 2.81:1 -> #C5410A */
      '.as-discount-price{color:#C5410A !important;}',
      /* "Best seller" card label: #9e9e9e = 2.68:1 -> #6E6E6E = 5.1:1 */
      '.as-link-wrap{color:#6E6E6E !important;}',
      /* PDP product-lineup sale price (orange on dark #333): 4.04:1 -> #FF8A4D = 5.4:1 */
      '.as-product-lineup__price{color:#FF8A4D !important;}',
      /* "You may like" review count: #8f8f8f = 3.23:1 -> #6E6E6E = 5.1:1 */
      '.as-you-may-like__reviews{color:#6E6E6E !important;}',
      /* Rebuy cart discount message: orange #ff5501 on #f0f0f0 = 2.81:1 -> #B83A00 = 5.06:1 */
      '#rebuy-cart [class*="discount-message"],.rebuy-cart__flyout-item-discount-message{color:#B83A00 !important;}'
    ].join('\n');
    var style = document.createElement('style');
    style.id = 'as-a11y-contrast';
    style.textContent = css;
    (document.body || document.documentElement).appendChild(style);
  }

  /* 2. Star ratings carry aria-label on a role-less <span> (invalid ARIA, SC
        4.1.2). The theme's inline script strips role, so re-assert role="img".
        The role-attribute observer below re-heals this if it is stripped. */
  function fixStarRoles() {
    document
      .querySelectorAll('.jdgm-prev-badge__stars,.jdgm-rev__rating,.jdgm-carousel-item__review-rating')
      .forEach(function (el) {
        if (el.getAttribute('aria-label') && el.getAttribute('role') !== 'img') {
          el.setAttribute('role', 'img');
        }
      });
  }

  /* 3. Cart quantity inputs have no accessible name (SC 1.3.1/3.3.2/4.1.2).
        Label each from its line-item product title. */
  function fixCartQty() {
    document.querySelectorAll('input[id^="MinimogQuantity"]').forEach(function (input) {
      if (input.getAttribute('aria-label')) return;
      var item = input.closest('.m-cart-item, tr, li');
      var titleEl = item && item.querySelector('a[href*="/products/"]');
      var title = titleEl ? titleEl.textContent.trim().replace(/\s+/g, ' ') : '';
      input.setAttribute('aria-label', title ? 'Quantity for ' + title : 'Quantity');
    });
  }

  /* 4. Faceted price-filter inputs have no label (SC 1.3.1/4.1.2). */
  function fixFacetPrice() {
    var map = [
      ['input[name="filter.v.price.gte"]', 'Minimum price'],
      ['input[name="filter.v.price.lte"]', 'Maximum price'],
      ['.m-facets-price--range-min', 'Minimum price slider'],
      ['.m-facets-price--range-max', 'Maximum price slider']
    ];
    map.forEach(function (pair) {
      document.querySelectorAll(pair[0]).forEach(function (i) {
        if (!i.getAttribute('aria-label')) i.setAttribute('aria-label', pair[1]);
      });
    });
  }

  /* 5. Product-showcase dots: a labelled <div> with no role (SC 4.1.2). */
  function fixShowcaseDots() {
    document
      .querySelectorAll('.as-product-showcase__dots[aria-label]:not([role])')
      .forEach(function (el) {
        el.setAttribute('role', 'group');
      });
  }

  /* 6. Images with no alt attribute (SC 1.1.1). Derive a name from the product
        title when the image sits in a product link/card; otherwise mark it
        decorative with an explicit empty alt. */
  function fixImageAlts() {
    document.querySelectorAll('img:not([alt])').forEach(function (img) {
      var link = img.closest('a[href*="/products/"]');
      var card = img.closest('[class*="product-card"],[class*="product-lineup"],[class*="you-may"]');
      var alt = '';
      if (link || card) {
        var titleEl = card && card.querySelector('[class*="title"],[class*="name"]');
        var src = titleEl || link;
        if (src) alt = src.textContent.trim().replace(/\s+/g, ' ').slice(0, 120);
      }
      img.setAttribute('alt', alt);
    });
  }

  /* 7. Rebuy Smart Cart drawer. When closed (no is-visible class / aria-hidden
        =true) it keeps ~38 focusable controls in the tab order (SC 4.1.2), so
        apply `inert`. When open it MUST be fully interactive, so remove `inert`.
        A dedicated, non-debounced observer on the drawer guarantees the OPEN
        cart is never left inert (a debounced/global observer raced Rebuy's open
        and could strand it). The open test trusts is-visible OR aria-hidden so a
        lag in either signal still un-inerts. Also wrap line-item <li>s in a list
        container (SC 1.3.1). */
  function syncRebuyInert(rc) {
    var open = rc.classList.contains('is-visible') || rc.getAttribute('aria-hidden') === 'false';
    if (open) {
      if (rc.hasAttribute('inert')) rc.removeAttribute('inert');
    } else if (!rc.hasAttribute('inert')) {
      rc.setAttribute('inert', '');
    }
  }
  var rebuyObserved = false;
  function fixRebuy() {
    var rc = document.getElementById('rebuy-cart');
    if (!rc) return;
    syncRebuyInert(rc);
    if (!rebuyObserved) {
      rebuyObserved = true;
      new MutationObserver(function () { syncRebuyInert(rc); }).observe(rc, {
        attributes: true,
        attributeFilter: ['aria-hidden', 'class']
      });
    }
    rc.querySelectorAll('li').forEach(function (li) {
      var p = li.parentElement;
      if (p && p.tagName !== 'UL' && p.tagName !== 'OL' && p.getAttribute('role') !== 'list') {
        p.setAttribute('role', 'list');
      }
    });
  }

  function runAll() {
    [injectContrastCSS, fixStarRoles, fixCartQty, fixFacetPrice, fixShowcaseDots, fixImageAlts, fixRebuy]
      .forEach(function (fn) { try { fn(); } catch (e) { /* never let one fix block the others */ } });
  }

  /* Run now, on load, on a few timed retries (covers the theme's own delayed
     a11y passes), and continuously via a debounced observer. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAll);
  } else {
    runAll();
  }
  window.addEventListener('load', function () {
    runAll();
    setTimeout(runAll, 600);
    setTimeout(runAll, 1800);
    setTimeout(runAll, 3200);
  });

  var scheduled = false;
  var raf = window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    raf(function () { scheduled = false; runAll(); });
  }
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['role', 'aria-hidden']
  });
})();
